import React, { useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../auth/hooks/useAuth';
import { LoginPage } from './LoginPage';
import { useNavigate, useLocation } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * ProtectedRoute Component
 * 
 * Wraps components that require authentication
 * Shows login page if user is not authenticated
 * Shows loading spinner while checking authentication status
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  fallback,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  console.log('ProtectedRoute render - isAuthenticated:', isAuthenticated, 'isLoading:', isLoading, 'path:', location.pathname);

  // Auto-navigate when authentication state changes
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      console.log('User is authenticated, current path:', location.pathname);
      // Only navigate if we're on the root path or login path
      if (location.pathname === '/' || location.pathname === '/login') {
        console.log('Navigating to dashboard');
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, navigate, location.pathname]);

  const handleLoginSuccess = () => {
    console.log('Login success handler called');
    // The useEffect above will handle navigation
  };

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Verificando autenticación...
        </Typography>
      </Box>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return fallback || <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  // Show protected content if authenticated
  return <>{children}</>;
};